import { type Message } from '@pubnub/chat';
import { useEffect } from 'react';
import { useChannelIds, useConversationsStore } from '@avanta/shared/stores/chat/conversations';
import { useChatStore } from '@avanta/shared/stores/chat/index';
import { useNotificationsStore } from '@avanta/shared/stores/chat/notifications';

export const useGlobalUnreadListener = (): void => {
  const chat = useChatStore(state => state.chat);
  const isInitialized = useChatStore(state => state.isInitialized);
  const loadConversations = useConversationsStore(state => state.loadConversations);
  const channelIds = useChannelIds();

  useEffect(() => {
    if (!isInitialized) return;
    loadConversations();
  }, [isInitialized, loadConversations]);

  useEffect(() => {
    if (!chat) return;

    if (channelIds.length === 0) return;

    let cancelled = false;
    const unsubscribers: (() => void)[] = [];

    const setup = async (): Promise<void> => {
      const connectionTimetoken = BigInt(Date.now()) * 10000n;

      await Promise.allSettled(
        channelIds.map(async channelId => {
          const channel = await chat.getChannel(channelId);
          if (!channel || cancelled) return;

          const handleIncomingMessage = (message: Message): void => {
            if (cancelled) return;
            const { updateUnreadCount } = useConversationsStore.getState();
            const { playNotificationSound } = useNotificationsStore.getState();

            const isNewMessage = BigInt(message.timetoken) > connectionTimetoken;
            const isFromOtherUser = message.userId !== chat.currentUser.id;

            if (isFromOtherUser && isNewMessage) {
              updateUnreadCount(channelId, 1, String(message.timetoken));
              playNotificationSound();
            }
          };

          const unsub = channel.connect(handleIncomingMessage);
          unsubscribers.push(unsub);
        })
      );
    };

    setup();

    return (): void => {
      cancelled = true;
      unsubscribers.forEach(unsub => unsub());
    };
  }, [channelIds, chat]);
};
